import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Store, select } from "@ngrx/store";
import { first } from "rxjs";
import { IprAnswerRecord, OrcadentQuestionnaireDataRecord, OrcadentQuestionnaireSection, Quadrant, RemoveAttachmentsForMovementAnswerRecord, RemoveAttachmentsPreAlignerAnswerRecord } from "libs/questionaire/src/lib/orcadent/models/questionnaire-orcadent.model";
import { selectAnamneseQuestionnaireOrcadentAnswers } from "libs/anamnese/src/lib/store/anamnese.selectors";
import { AppState } from "libs/tm-common/src/lib/store/reducers/reducers";
import { ACTION } from "libs/tm-common/src/lib/constants/subcomponent.actions";


@Component({
	selector: "tm2-anamnese-questionnaire-orcadent",
	templateUrl: "./anamnese-questionnaire-orcadent.component.html",
	styleUrl: "./anamnese-questionnaire-orcadent.component.scss"
})
export class AnamneseQuestionnaireOrcadentComponent implements OnInit{
	@Output() next = new EventEmitter<any>();


	OrcadentQuestionnaireSection = OrcadentQuestionnaireSection;
	requiredOptionTextFormGroup = {
		option: [null, Validators.required],
		text: [null]
	}

	optionTextFormGroup = {
		option: [null],
		text: [null]
	}

	questionnaireForm: FormGroup = this.formBuilder.group( {
		preAlignerDistractionSplint: [null, Validators.required],
		removeAttachmentsPreAligner: this.formBuilder.group({
			uj: this.formBuilder.group(this.requiredOptionTextFormGroup),
			lj: this.formBuilder.group(this.requiredOptionTextFormGroup),
		}),
		occlusalHeightInstruction: this.formBuilder.group(this.requiredOptionTextFormGroup),
		nonMovingTeeth: [null],
		middleline: this.formBuilder.group({
			uj: this.formBuilder.group(this.requiredOptionTextFormGroup),
			lj: this.formBuilder.group(this.requiredOptionTextFormGroup),
		}),
		anteriorTeeth: this.formBuilder.group({
			uj: this.formBuilder.group({
				protrude_retrude: this.formBuilder.group(this.optionTextFormGroup),
				procline_recline: this.formBuilder.group(this.optionTextFormGroup),
				compress_expand:  this.formBuilder.group(this.optionTextFormGroup),
				torque:  this.formBuilder.group(this.optionTextFormGroup),
			}),
			lj: this.formBuilder.group({
				protrude_retrude: this.formBuilder.group(this.optionTextFormGroup),
				procline_recline: this.formBuilder.group(this.optionTextFormGroup),
				compress_expand:  this.formBuilder.group(this.optionTextFormGroup),
				torque:  this.formBuilder.group(this.optionTextFormGroup),
			}),
		}),
		posteriorTeeth: this.formBuilder.group({
			q1: this.formBuilder.group({
				distalize_mesialize: this.formBuilder.group(this.optionTextFormGroup),
				compress_expand: this.formBuilder.group(this.optionTextFormGroup),
			}),
			q2: this.formBuilder.group({
				distalize_mesialize: this.formBuilder.group(this.optionTextFormGroup),
				compress_expand: this.formBuilder.group(this.optionTextFormGroup),
			}),
			q3: this.formBuilder.group({
				distalize_mesialize: this.formBuilder.group(this.optionTextFormGroup),
				compress_expand: this.formBuilder.group(this.optionTextFormGroup),
			}),
			q4: this.formBuilder.group({
				distalize_mesialize: this.formBuilder.group(this.optionTextFormGroup),
				compress_expand: this.formBuilder.group(this.optionTextFormGroup),
			}),
		}),
		removeAttachmentsForMovement: this.formBuilder.group({
			uj: this.formBuilder.group(this.requiredOptionTextFormGroup),
			lj: this.formBuilder.group(this.requiredOptionTextFormGroup),
		}),
		attachmentPositionMovement: this.formBuilder.group({
			uj: this.formBuilder.group({
				scheuProtocol: [true],
				labialBuccal: [null],
				lingualPalatal: [null],
			}),
			lj: this.formBuilder.group({
				scheuProtocol: [true],
				labialBuccal: [null],
				lingualPalatal: [null],
			}),
		}),
		ipr: this.formBuilder.group({
			uj: this.formBuilder.group(this.requiredOptionTextFormGroup),
			lj: this.formBuilder.group(this.requiredOptionTextFormGroup),
		}),
		elasticCuts: [null],
		buttonCuts: [null],
		pontics: this.formBuilder.group({
			half: [null],
			full: [null],
		}),
		eruptionCaps: this.formBuilder.group({
			q1: [false],
			q2: [false],
			q3: [false],
			q4: [false],
		}),
		individualComments: [null]
	} )

	RemoveAttachmentsPreAlignerAnswerRecord = RemoveAttachmentsPreAlignerAnswerRecord;
	RemoveAttachmentsForMovementAnswerRecord = RemoveAttachmentsForMovementAnswerRecord;
	IprAnswerRecord = IprAnswerRecord;
	Quadrant = Quadrant;

	constructor(
		private formBuilder: FormBuilder,
		private store: Store<AppState>){
	}

	ngOnInit(): void {
		this.store.pipe(select(selectAnamneseQuestionnaireOrcadentAnswers), first())
			.subscribe(answers => {
				if(answers){
					this.questionnaireForm.patchValue( answers);
				}
			});
	}

	protected checkValid() : boolean{
		return this.questionnaireForm.valid;
	}

	onAction($event) {
		switch ($event.type) {
			case ACTION.NEXT:
				let payload = this.questionnaireForm.value;
				if(this.preAlignerDistractionSplint.value === false){
					payload = {
						...payload,
						removeAttachmentsPreAligner: {
							uj: {option: null},
							lj: {option: null},
						}
					}
				}
				this.next.emit({ type: ACTION.QUESTIONAIRE, payload });
				break;
			case ACTION.BACK:
				this.next.emit({ type: ACTION.BACK });

		}
	}

	protected getHeadline(section: OrcadentQuestionnaireSection): string{
		return OrcadentQuestionnaireDataRecord[section].headline;
	}

	get preAlignerDistractionSplint(){
		return this.questionnaireForm.get('preAlignerDistractionSplint');
	}

	get removeAttachmentsPreAligner(){
		return this.questionnaireForm.get('removeAttachmentsPreAligner');
	}

	get occlusalHeightInstruction(){
		return this.questionnaireForm.get('occlusalHeightInstruction');
	}

	get removeAttachmentsForMovement(){
		return this.questionnaireForm.get('removeAttachmentsForMovement');
	}
	get nonMovingTeeth(){
		return this.questionnaireForm.get('nonMovingTeeth');
	}

	get middleline(){
		return this.questionnaireForm.get('middleline');
	}

	get attachmentPositionMovement(){
		return this.questionnaireForm.get('attachmentPositionMovement');
	}

	get ipr(){
		return this.questionnaireForm.get('ipr');
	}

	get anteriorTeeth(){
		return this.questionnaireForm.get('anteriorTeeth');
	}

	get posteriorTeeth(){
		return this.questionnaireForm.get('posteriorTeeth');
	}
}
